import { QUIZ_SECTION_LABEL_MAX } from './quizHeader';
import {
  sectionNodePositions,
  sectionProgressState,
  visitorSectionPath,
  type VisitorSectionInput,
} from './sectionProgress';

/**
 * Шаги-разделы шапки воронки (`settings.quizHeader.progress:
 * 'section-nodes'`, слово владельца 21.10.2026, Blinkist): узел на каждый
 * раздел пути посетителя — счёт и путь те же, что у узлов пака
 * (`sectionProgress.ts`), своего нет. Сверху — состояние узла (пройден,
 * текущий, будущий) и подпись текущего раздела: поле экрана
 * `progressSectionLabel` ПЕРВОГО экрана раздела (подпись на других экранах
 * раздела не берётся).
 */
export type SectionStepState = 'done' | 'current' | 'idle';

export interface SectionStepsState {
  nodes: SectionStepState[];
  /** Номер текущего узла; −1 — узлов нет. */
  current: number;
  /** Подпись текущего раздела; нет — подписи нет. */
  label?: string;
}

/**
 * Зажим подписи: ручной сейв кабинета идёт мимо схемы сервера, поэтому
 * негодное (не строка, пусто, длиннее потолка) — как нет поля.
 */
export function readSectionLabel(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const v = raw.trim();
  return v !== '' && raw.length <= QUIZ_SECTION_LABEL_MAX ? v : undefined;
}

/**
 * Состояние узлов по позициям разделов в пути и позиции `current` — та же
 * дверь `sectionProgressState`, что у узлов пака. `endAt` — позиция конечного
 * узла «финиш» (за последним экраном пути, задание stylepack/section-steps-end,
 * Blinkist): он заполнен, когда пройден последний раздел, иначе будущий;
 * текущим остаётся раздел. Без разделов финиша нет.
 */
export function sectionStepStates(
  positions: readonly number[],
  current: number,
  endAt?: number,
): Pick<SectionStepsState, 'nodes' | 'current'> {
  const withEnd =
    endAt !== undefined && positions.length > 0
      ? [...positions, endAt]
      : positions;
  const { nodes } = sectionProgressState(withEnd, current);
  const k = nodes.slice(0, positions.length).lastIndexOf(true);
  return {
    nodes: nodes.map((on, i) => (i === k ? 'current' : on ? 'done' : 'idle')),
    current: k,
  };
}

export function sectionStepsForVisitor(
  input: VisitorSectionInput,
  { endNode }: { endNode?: boolean } = {},
): SectionStepsState {
  const { path, current } = visitorSectionPath(input);
  const positions = sectionNodePositions(path);
  const steps = sectionStepStates(
    positions,
    current,
    endNode === true ? path.length : undefined,
  );
  const k = steps.current;
  return {
    ...steps,
    label:
      k >= 0
        ? readSectionLabel(path[positions[k]]?.progressSectionLabel)
        : undefined,
  };
}

/**
 * Отрезок полосы по разделам (`progress: 'section-bars'`, задание
 * stylepack/section-bars, Lovi): вес — доля ширины, заливка — 0..1.
 */
export interface SectionBar {
  weight: number;
  fill: number;
}

/**
 * Отрезки по разделам: вес отрезка — экранов в разделе (позиции разделов —
 * `sectionNodePositions`), заливка — та же дверь `sectionProgressState`, что
 * у узлов, с концом пути последней позицией. Текущий экран засчитан, как у
 * полосы `bar` (номер экрана / число экранов): пройденные разделы целиком,
 * текущий — по доле, будущие — пусто. Без разделов — один отрезок на весь
 * путь (как `bar`); пустой путь — отрезков нет.
 */
export function sectionBarStates(
  positions: readonly number[],
  current: number,
  length: number,
): SectionBar[] {
  if (length <= 0) return [];
  const ends = [...(positions.length > 0 ? positions : [0]), length];
  const { fills } = sectionProgressState(ends, current + 1);
  return fills.map((fill, k) => ({ weight: ends[k + 1] - ends[k], fill }));
}

/**
 * Отрезки по своим пропорциям (`progressSegmentWeights`): ход `done / total`
 * идёт последовательно через отрезки — отрезок с долей W из суммы получает
 * свою часть хода. Разделы не нужны.
 */
export function weightedBarStates(
  weights: readonly number[],
  done: number,
  total: number,
): SectionBar[] {
  const sum = weights.reduce((a, w) => a + w, 0);
  const p = total > 0 && sum > 0 ? done / total : 0;
  let start = 0;
  return weights.map((weight) => {
    const share = weight / sum;
    const fill = Math.min(1, Math.max(0, (p - start) / share));
    start += share;
    return { weight, fill };
  });
}

/** Путь посетителя (та же дверь, что у шагов-разделов) → отрезки полосы. */
export function sectionBarsForVisitor(
  input: VisitorSectionInput,
  { weights }: { weights?: readonly number[] } = {},
): SectionBar[] {
  const { path, current } = visitorSectionPath(input);
  if (weights && weights.length > 0) {
    return weightedBarStates(weights, current + 1, path.length);
  }
  return sectionBarStates(sectionNodePositions(path), current, path.length);
}
